export interface InteractionLockOptions {
  scroll?: boolean;
  scrollTarget?: HTMLElement | Document | Window | null;
  selection?: boolean;
}

interface StyleSnapshot {
  htmlOverflow: string;
  htmlOverscrollBehavior: string;
  htmlTouchAction: string;
  htmlUserSelect: string;
  htmlWebkitUserSelect: string;
  bodyOverflow: string;
  bodyOverscrollBehavior: string;
  bodyTouchAction: string;
  bodyUserSelect: string;
  bodyWebkitUserSelect: string;
}

interface TargetSnapshot {
  target: HTMLElement;
  overflow: string;
  overscrollBehavior: string;
  touchAction: string;
  count: number;
}

let scrollLocks = 0;
let pageScrollLocks = 0;
let selectionLocks = 0;
let snapshot: StyleSnapshot | null = null;
const targetSnapshots = new Map<HTMLElement, TargetSnapshot>();

function captureSnapshot(): StyleSnapshot {
  const html = document.documentElement;
  const body = document.body;

  return {
    htmlOverflow: html.style.overflow,
    htmlOverscrollBehavior: html.style.overscrollBehavior,
    htmlTouchAction: html.style.touchAction,
    htmlUserSelect: html.style.userSelect,
    htmlWebkitUserSelect: html.style.webkitUserSelect,
    bodyOverflow: body.style.overflow,
    bodyOverscrollBehavior: body.style.overscrollBehavior,
    bodyTouchAction: body.style.touchAction,
    bodyUserSelect: body.style.userSelect,
    bodyWebkitUserSelect: body.style.webkitUserSelect,
  };
}

function ensureSnapshot(): void {
  if (!snapshot) {
    snapshot = captureSnapshot();
  }
}

function resolveScrollTarget(target: InteractionLockOptions["scrollTarget"]): HTMLElement | null {
  if (!target || target === window || target === document) {
    return null;
  }
  return target;
}

function preventTouchScroll(event: TouchEvent): void {
  if (event.cancelable) {
    event.preventDefault();
  }
}

function lockScrollTarget(target: HTMLElement): void {
  const existing = targetSnapshots.get(target);
  if (existing) {
    existing.count += 1;
  } else {
    targetSnapshots.set(target, {
      target,
      overflow: target.style.overflow,
      overscrollBehavior: target.style.overscrollBehavior,
      touchAction: target.style.touchAction,
      count: 1,
    });
  }

  target.style.overflow = "hidden";
  target.style.overscrollBehavior = "none";
  target.style.touchAction = "none";
}

function unlockScrollTarget(target: HTMLElement): void {
  const existing = targetSnapshots.get(target);
  if (!existing) return;

  existing.count -= 1;
  if (existing.count > 0) return;

  target.style.overflow = existing.overflow;
  target.style.overscrollBehavior = existing.overscrollBehavior;
  target.style.touchAction = existing.touchAction;
  targetSnapshots.delete(target);
}

function applyLocks(scrollTarget: HTMLElement | null): void {
  const html = document.documentElement;
  const body = document.body;

  if (selectionLocks > 0) {
    html.style.userSelect = "none";
    html.style.webkitUserSelect = "none";
    body.style.userSelect = "none";
    body.style.webkitUserSelect = "none";
  }

  if (scrollLocks > 0) {
    html.style.overscrollBehavior = "none";
    html.style.touchAction = "none";
    body.style.overscrollBehavior = "none";
    body.style.touchAction = "none";
    if (scrollTarget) {
      lockScrollTarget(scrollTarget);
    } else {
      pageScrollLocks += 1;
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    }
    window.addEventListener("touchmove", preventTouchScroll, { passive: false });
  }
}

function syncLocks(releasedScrollTarget: HTMLElement | null): void {
  if (!snapshot) {
    return;
  }

  const html = document.documentElement;
  const body = document.body;

  if (releasedScrollTarget) {
    unlockScrollTarget(releasedScrollTarget);
  } else if (pageScrollLocks > 0) {
    pageScrollLocks -= 1;
  }

  if (pageScrollLocks === 0) {
    html.style.overflow = snapshot.htmlOverflow;
    body.style.overflow = snapshot.bodyOverflow;
  }

  if (scrollLocks === 0) {
    html.style.overscrollBehavior = snapshot.htmlOverscrollBehavior;
    html.style.touchAction = snapshot.htmlTouchAction;
    body.style.overscrollBehavior = snapshot.bodyOverscrollBehavior;
    body.style.touchAction = snapshot.bodyTouchAction;
    window.removeEventListener("touchmove", preventTouchScroll);
  }

  if (selectionLocks === 0) {
    html.style.userSelect = snapshot.htmlUserSelect;
    html.style.webkitUserSelect = snapshot.htmlWebkitUserSelect;
    body.style.userSelect = snapshot.bodyUserSelect;
    body.style.webkitUserSelect = snapshot.bodyWebkitUserSelect;
  }

  if (scrollLocks === 0 && selectionLocks === 0) {
    snapshot = null;
  }
}

export function lockPageInteraction(options: InteractionLockOptions = {}): () => void {
  const lockScroll = options.scroll === true;
  const lockSelection = options.selection === true;
  const scrollTarget = lockScroll ? resolveScrollTarget(options.scrollTarget) : null;

  if (!lockScroll && !lockSelection) {
    return () => {};
  }

  ensureSnapshot();

  if (lockScroll) {
    scrollLocks += 1;
  }
  if (lockSelection) {
    selectionLocks += 1;
  }

  applyLocks(scrollTarget);

  let released = false;
  return () => {
    if (released) return;
    released = true;

    if (lockScroll) {
      scrollLocks = Math.max(0, scrollLocks - 1);
    }
    if (lockSelection) {
      selectionLocks = Math.max(0, selectionLocks - 1);
    }

    syncLocks(scrollTarget);
  };
}
